import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

interface AnimatedNumberProps {
  value: number | null | undefined;
  /** Custom formatter. Receives the (intermediate) numeric value. Default = `Intl.NumberFormat`. */
  format?: (n: number) => string;
  /** Animation duration in ms. Defaults to 600ms — slow enough to be perceived, fast enough to feel snappy. */
  duration?: number;
  className?: string;
  /** When `value` is nullish, render this in place. Defaults to '—'. */
  placeholder?: string;
  /**
   * If true, the very first mount is animated from 0. Default: false (no jarring count-up on hydrate).
   * Subsequent value changes always animate.
   */
  animateMount?: boolean;
}

const defaultFormat = (n: number) => new Intl.NumberFormat('en-IN').format(Math.round(n));

/**
 * Smoothly interpolates from the previous value to the next using rAF.
 *
 * Per Emil:
 *   - Eases with our strong custom curve (ease-out) so the change feels
 *     intentional rather than mechanical.
 *   - Uses `font-variant-numeric: tabular-nums` so digits don't reflow.
 *   - Honors `prefers-reduced-motion`: jumps directly to the new value.
 *
 * Costless when value doesn't change — the rAF loop bails on the first frame.
 */
export function AnimatedNumber({
  value,
  format = defaultFormat,
  duration = 600,
  className,
  placeholder = '—',
  animateMount = false,
}: AnimatedNumberProps) {
  const [display, setDisplay] = useState<number>(() => (animateMount ? 0 : (value ?? 0)));
  const fromRef = useRef<number>(animateMount ? 0 : (value ?? 0));
  const targetRef = useRef<number>(value ?? 0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (value == null || Number.isNaN(value)) return;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduce) {
      setDisplay(value);
      fromRef.current = value;
      targetRef.current = value;
      return;
    }

    const start = performance.now();
    const from = fromRef.current;
    const to = value;
    targetRef.current = to;
    if (from === to) return;

    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutExpo(t);
      const v = from + (to - from) * eased;
      setDisplay(v);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
        rafRef.current = null;
      }
    };

    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [value, duration]);

  if (value == null || Number.isNaN(value)) {
    return <span className={cn('tabular-nums', className)}>{placeholder}</span>;
  }
  return <span className={cn('tabular-nums', className)}>{format(display)}</span>;
}

// Strong ease-out (curve mirrors `--ease-out` in tokens.css).
function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}
